import {
  Box,
  Button,
  Divider,
  Skeleton,
  styled,
  Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CustomButton } from "@/components/custom/custom-button";
import { useChannelTrendStats } from "@/hooks/use-channel-trend-stats";
import { fmtVolume } from "../overview";

const Card = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2.5),
}));

const PeriodSelector = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.08)"
      : theme.palette.grey[100],
  borderRadius: 20,
  padding: 4,
  gap: 2,
}));

const PeriodBtn = styled(Button, {
  shouldForwardProp: (p) => p !== "isActive",
})(({ theme, isActive }) => ({
  minWidth: 44,
  height: 30,
  borderRadius: 20,
  fontSize: "0.78rem",
  textTransform: "none",
  fontWeight: isActive ? 600 : 400,
  backgroundColor: isActive ? theme.palette.primary.main : "transparent",
  color: isActive ? "#fff" : theme.palette.text.secondary,
  "&:hover": {
    backgroundColor: isActive
      ? theme.palette.primary.main
      : theme.palette.action.hover,
  },
}));

const ChartLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: theme.palette.text.secondary,
  marginBottom: 4,
  paddingLeft: 4,
}));

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const volume = payload.find((p) => p.dataKey === "volume")?.value ?? null;
  if (volume === null) return null;

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "0.5px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        p: 1.5,
        minWidth: 148,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        mb={0.75}
      >
        {label}
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 3 }}>
        <Typography variant="caption" color="text.secondary">
          Volume
        </Typography>
        <Typography variant="caption" fontWeight={500}>
          {fmtVolume(volume)}
        </Typography>
      </Box>
    </Box>
  );
};

const PERIODS = ["7d", "14d", "30d", "60d", "90d"];
const TICK = { fontSize: 11, fill: "#888780" };

const TransactionTrend = ({ productId, period, onPeriodChange }) => {
  const { data, isLoading: loading } = useChannelTrendStats(
    period,
    productId,
  );
  const series = data?.series;
  const xInterval = series?.length > 10 ? Math.ceil(series.length / 8) : 0;

  return (
    <Card>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography fontWeight={600} fontSize="1.05rem">
            Transaction Trend
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {data?.label || period}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <PeriodSelector>
            {PERIODS.map((p) => (
              <PeriodBtn
                key={p}
                isActive={period === p}
                onClick={() => onPeriodChange(p)}
              >
                {p}
              </PeriodBtn>
            ))}
          </PeriodSelector>
          <Divider orientation="vertical" flexItem />
          <CustomButton
            variantType="outline"
            endIcon={<FileDownloadOutlinedIcon fontSize="small" />}
            sx={{ height: 34 }}
            onClick={() => console.log("export")}
          >
            Export
          </CustomButton>
        </Box>
      </Box>

      {loading ? (
        <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2 }} />
      ) : !series ? null : (
        <>
          <ChartLabel>Volume trend</ChartLabel>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart
              data={series}
              margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(128,128,128,0.1)"
                vertical={false}
              />

              <XAxis
                dataKey="label"
                tick={TICK}
                axisLine={false}
                tickLine={false}
                interval={xInterval}
              />

              <YAxis
                tickFormatter={fmtVolume}
                tick={{ ...TICK, fill: "#534AB7" }}
                axisLine={false}
                tickLine={false}
                width={56}
              />

              <Tooltip content={<ChartTooltip />} />

              <Line
                dataKey="volume"
                name="Volume"
                type="monotone"
                stroke="#534AB7"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </>
      )}
    </Card>
  );
};

export default TransactionTrend;
